/**
 * Mirrors CANONICAL_SCAN_REPORT_V1_LIVE_PDF_ALIASES in
 * safety_control_review_canonical_export.py — near_miss / forklift family
 * uses the compact buyer face for hosted HTML and compact PDF.
 */
export const COMPACT_BUYER_FACE_ALIASES = new Set([
  'forklift_proximity',
  'near_miss',
  'pedestrian_near_moving_forklift',
]);

function normalizePolicyAlias(value: unknown): string {
  const raw = String(value || '').trim().toLowerCase();
  if (!raw) return '';
  return raw.replace(/[^a-z0-9]+/g, '_').replace(/^_+|_+$/g, '');
}

function collectAliasSources(record: Record<string, unknown> | null | undefined): Record<string, unknown>[] {
  if (!record || typeof record !== 'object') return [];
  const sources: Record<string, unknown>[] = [record];
  const nestedKeys = ['report_payload', 'report_content', 'finalized_payload', 'workflow_anchor'];
  nestedKeys.forEach((key) => {
    const nested = record[key];
    if (nested && typeof nested === 'object' && !Array.isArray(nested)) {
      sources.push(nested as Record<string, unknown>);
    }
  });
  sources.forEach((source) => {
    ['scope', 'report_context', 'ui_context', 'policy_report_context'].forEach((key) => {
      const nested = source[key];
      if (nested && typeof nested === 'object' && !Array.isArray(nested)) {
        sources.push(nested as Record<string, unknown>);
      }
    });
  });
  return sources;
}

function aliasesFromSources(sources: Record<string, unknown>[]): Set<string> {
  const aliases = new Set<string>();
  const keys = ['policy_alias', 'policy_type', 'risk_type', 'incident_type'];
  sources.forEach((source) => {
    keys.forEach((key) => {
      const normalized = normalizePolicyAlias(source[key]);
      if (normalized) aliases.add(normalized);
    });
  });
  return aliases;
}

export function usesCompactBuyerFace(input?: {
  riskType?: string | null;
  policyAlias?: string | null;
  policyType?: string | null;
  persistedReport?: Record<string, unknown> | null;
  artifactScope?: Record<string, unknown> | null;
  policyReportContext?: Record<string, unknown> | null;
}): boolean {
  const aliases = new Set<string>();
  [
    input?.riskType,
    input?.policyAlias,
    input?.policyType,
    input?.artifactScope?.risk_type,
    input?.artifactScope?.policy_alias,
    input?.artifactScope?.policy_type,
    input?.artifactScope?.incident_type,
    input?.policyReportContext?.policy_alias,
    input?.policyReportContext?.policy_type,
    input?.policyReportContext?.risk_type,
  ].forEach((value) => {
    const normalized = normalizePolicyAlias(value);
    if (normalized) aliases.add(normalized);
  });

  if (input?.persistedReport) {
    aliasesFromSources(collectAliasSources(input.persistedReport)).forEach((alias) => aliases.add(alias));
  }

  return Array.from(aliases).some((alias) => COMPACT_BUYER_FACE_ALIASES.has(alias));
}
